import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Gift, Pin } from 'lucide-react'
import { MUSIC_HOW, achieveApi, type Achievement } from '@/lib/achieve'
import { authApi, describeError } from '@/lib/auth'
import { decorApi } from '@/lib/decor'
import { avatarUrl } from '@/lib/avatar'
import { cx } from '@/lib/cx'
import { useAuthStore } from '@/store/auth'
import { Avatar } from '../Avatar'
import { InfoTip } from '../InfoTip'
import { RewardsModal } from './RewardsModal'
import { AchievementIcon } from '../AchievementIcon'
import layer from './SettingsLayer.module.css'
import styles from './AchievementsSection.module.css'

const date = (iso: string) => new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })

/** Как пользоваться музыкальным ботом — пояснение у «Диджея». */
/**
 * «Достижения» — в настройках и отдельной страницей: что уже получено, сколько осталось и что за это
 * дают. Нажатие на достижение показывает награды на вас. page — страница со своим заголовком сверху.
 */
export function AchievementsSection({ page = false }: { page?: boolean }) {
  const { data, error } = useQuery({ queryKey: ['achievements'], queryFn: achieveApi.list })
  const decor = useQuery({ queryKey: ['decorations'], queryFn: decorApi.inventory })
  const queryClient = useQueryClient()
  const me = useAuthStore((s) => s.user)
  const [pinError, setPinError] = useState<string | null>(null)
  const [preview, setPreview] = useState<Achievement | null>(null)
  const picked = decor.data?.current.profile_showcase ?? []
  // витрина: до трёх полученных, сохраняется сразу
  const togglePin = async (id: string) => {
    const next = picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]
    setPinError(null)
    try {
      await authApi.updateProfile({ profile_showcase: next })
      await queryClient.invalidateQueries({ queryKey: ['decorations'] })
      if (me) await queryClient.invalidateQueries({ queryKey: ['profile', me.id] })
    } catch (err) {
      setPinError(describeError(err).message)
    }
  }
  const hasNew = Boolean(data?.some((a) => a.new))
  // раздел открыт — новые больше не новые; метки «Новое» остаются до следующего захода
  useEffect(() => {
    if (hasNew) void achieveApi.seen().catch(() => undefined)
  }, [hasNew])
  // ушли из раздела — список перечитывается, и счётчик новых у «Достижений» на главной пропадает
  useEffect(() => () => void queryClient.invalidateQueries({ queryKey: ['achievements'] }), [queryClient])
  const earned = data?.filter((a) => a.earned_at).length ?? 0
  const total = data?.length ?? 0

  return (
    <>
      <div className={styles.head}>
        <Ring earned={earned} total={total} />
        <div>
          {page ? null : <h1 className={layer.sectionTitle}>Достижения</h1>}
          <p className={layer.text}>За достижения открываются рамки, эффекты и стиль ника.</p>
          <p className={styles.hint}>Нажмите на достижение, чтобы примерить награды. До трёх полученных можно поставить на витрину в профиле.</p>
        </div>
      </div>
      {error ? <p className={styles.error}>{describeError(error).message}</p> : null}
      {pinError ? <p className={styles.error}>{pinError}</p> : null}
      <div className={styles.grid}>
        {data?.map((a) => <Card key={a.id} a={a} pinned={picked.includes(a.id)} canPin={picked.length < 3} onPin={() => void togglePin(a.id)} onOpen={() => setPreview(a)} />)}
      </div>
      {preview ? <RewardsModal achievement={preview} onClose={() => setPreview(null)} /> : null}
    </>
  )
}

function Ring({ earned, total }: { earned: number; total: number }) {
  const r = 34
  const c = 2 * Math.PI * r
  const share = total ? earned / total : 0
  return (
    <span className={styles.ring} aria-label={`Получено ${earned} из ${total}`}>
      <svg width="84" height="84" viewBox="0 0 84 84" aria-hidden="true">
        <circle cx="42" cy="42" r={r} className={styles.ringTrack} />
        <circle cx="42" cy="42" r={r} className={styles.ringValue} strokeDasharray={`${c * share} ${c}`} transform="rotate(-90 42 42)" />
      </svg>
      <span className={styles.ringText}>{earned}<span className={styles.ringTotal}>/{total}</span></span>
    </span>
  )
}

function Card({ a, pinned, canPin, onPin, onOpen }: { a: Achievement; pinned: boolean; canPin: boolean; onPin: () => void; onOpen: () => void }) {
  const user = useAuthStore((s) => s.user)
  const done = a.earned_at !== null
  const pct = a.goal ? Math.min(100, Math.round((a.progress / a.goal) * 100)) : 0
  return (
    // вся карточка открывает награды; «На витрину» и (i) — свои кнопки
    <div
      className={cx(styles.card, done && styles.cardDone)}
      role="button"
      tabIndex={0}
      aria-label={`Награды за «${a.name}»`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
    >
      <div className={styles.cardHead}>
        <AchievementIcon id={a.id} size={48} className={cx(!done && styles.iconIdle)} />
        <div className={styles.cardText}>
          <span className={styles.name}>
            {a.name}
            {a.id === 'dj_50' ? <InfoTip text={MUSIC_HOW} label="Как включить музыку" /> : null}
            {a.new ? <span className={styles.new}>Новое</span> : null}
          </span>
          <span className={styles.desc}>{a.description}</span>
        </div>
      </div>
      {done ? (
        <div className={styles.doneRow}>
          <span className={styles.done}><Check size={14} aria-hidden="true" /> Получено {date(a.earned_at!)}</span>
          <button type="button" className={cx(styles.pin, pinned && styles.pinOn)} aria-pressed={pinned} disabled={!pinned && !canPin} onClick={(e) => { e.stopPropagation(); onPin() }} onKeyDown={(e) => e.stopPropagation()} data-tooltip={!pinned && !canPin ? 'На витрине уже три' : undefined}>
            <Pin size={13} aria-hidden="true" /> {pinned ? 'На витрине' : 'На витрину'}
          </button>
        </div>
      ) : a.goal > 1 ? (
        <div className={styles.progress}>
          <span className={styles.count}>{a.progress} из {a.goal} {a.unit}</span>
          <span className={styles.bar}><span className={styles.barValue} style={{ width: `${pct}%` }} /></span>
        </div>
      ) : (
        <span className={styles.count}>Ещё не получено</span>
      )}
      {a.rewards.length ? (
        <div className={styles.rewards}>
          {a.rewards.map((r) => (
            <span key={r.item} className={styles.reward}>
              {r.item.startsWith('frame:') && user ? (
                <span className={styles.rewardArt}>
                  <Avatar initial={(user.global_name ?? user.username).slice(0, 1).toUpperCase()} color="#5865F2" size={16} src={avatarUrl(user.id, user.avatar)} decoration={{ asset: r.item.slice(6), sku_id: '0' }} />
                </span>
              ) : <Gift size={14} aria-hidden="true" />}
              {r.name}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  )
}
